// safety/incidents-and-injuries/components/tabs/LettersTab.tsx
import React, { useState } from 'react';
import { FileText, Pencil, Printer, RotateCcw, Send, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { SectionTitle, AddButton } from '../shared';
import {
    fmtDate,
    todayISO,
    uid,
    renderLetterBody,
    escapeHtml,
    LETTER_FIELDS,
    DEFAULT_LETTER_BODY,
} from '../../utils/incidents-utils';
import * as apiSvc from '../../../../../utils/safety-incidents-api';
import type { Incident, LetterTemplate } from '../../../../../types/incident.types';

const inputCls = 'w-full h-10 rounded-lg border border-border bg-muted/40 px-3 text-sm';

const Modal: React.FC<{
    title: string;
    onClose: () => void;
    children: React.ReactNode;
    footer: React.ReactNode;
    wide?: boolean;
}> = ({ title, onClose, children, footer, wide }) => (
    <div
        className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
    >
        <div className={`bg-card rounded-lg border border-border shadow-xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[85vh] flex flex-col`}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <h3 className="text-base font-semibold">{title}</h3>
                <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                    <X size={20} />
                </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">{children}</div>
            <div className="px-5 py-3.5 border-t border-border flex justify-end gap-2">{footer}</div>
        </div>
    </div>
);

const modalCancelBtn = 'h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent';
const modalPrimaryBtn = 'h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed';

export const LettersTab: React.FC<{
    incident: Incident;
    templates: LetterTemplate[];
    onSaveTemplate: (t: LetterTemplate) => void;
    onArchive: (id: string) => void;
}> = ({ incident, templates, onSaveTemplate, onArchive }) => {
    const [showArchived, setShowArchived] = useState(false);
    const [editing, setEditing] = useState<LetterTemplate | null>(null);
    const [preview, setPreview] = useState<LetterTemplate | null>(null);
    const [sendTarget, setSendTarget] = useState<LetterTemplate | null>(null);
    const [sendSubject, setSendSubject] = useState('');
    const [sending, setSending] = useState(false);

    const visible = showArchived ? templates : templates.filter((t) => !t.archived);
    const archivedCount = templates.filter((t) => t.archived).length;
    const isNewTemplate = editing !== null && !templates.some((t) => t.id === editing.id);
    const canSend = Boolean(incident.employeeId);

    const saveEditing = () => {
        if (!editing) return;
        if (!editing.name.trim()) {
            toast.error('Template name is required');
            return;
        }
        onSaveTemplate({ ...editing, name: editing.name.trim(), edited: todayISO() });
        setEditing(null);
    };

    const printLetter = (t: LetterTemplate) => {
        const w = window.open('', '_blank', 'width=800,height=900');
        if (!w) {
            toast.error('Please allow popups to print the letter');
            return;
        }
        w.document.write(`<!doctype html>
<html><head><title>${escapeHtml(t.name)}</title>
<style>body{font-family:Georgia,serif;margin:48px;line-height:1.6;color:#111}h1{font-size:18px;margin:0 0 24px}pre{font:inherit;white-space:pre-wrap;margin:0}</style>
</head><body><h1>${escapeHtml(t.name)}</h1><pre>${escapeHtml(renderLetterBody(t.body, incident))}</pre></body></html>`);
        w.document.close();
        w.focus();
        w.print();
    };

    const openSend = (t: LetterTemplate) => {
        setSendTarget(t);
        setSendSubject(t.name);
    };

    const doSend = async () => {
        if (!sendTarget || !sendSubject.trim()) return;
        setSending(true);
        try {
            const res = await apiSvc.sendLetter(incident.id, {
                subject: sendSubject.trim(),
                body: renderLetterBody(sendTarget.body, incident),
            });
            toast.success(res.message || 'Letter sent');
            setSendTarget(null);
        } catch (err: any) {
            toast.error(err?.message || 'Could not send letter');
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <SectionTitle
                title="Letter Templates"
                right={
                    <div className="flex items-center gap-2">
                        {archivedCount > 0 && (
                            <button
                                onClick={() => setShowArchived((v) => !v)}
                                className="h-9 px-3.5 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent"
                            >
                                {showArchived ? 'Hide Archived' : `Show Archived (${archivedCount})`}
                            </button>
                        )}
                        <AddButton
                            label="Add Template"
                            onClick={() => setEditing({ id: uid('tpl'), name: '', body: DEFAULT_LETTER_BODY, edited: todayISO(), archived: false })}
                        />
                    </div>
                }
            />

            <div className="space-y-2.5">
                {visible.length === 0 && (
                    <div className="rounded-lg border border-border px-4 py-10 text-center text-sm text-muted-foreground">
                        {templates.length === 0
                            ? 'No letter templates yet. Add your first template to generate letters.'
                            : 'No active templates. Use "Show Archived" to view archived templates.'}
                    </div>
                )}
                {visible.map((t) => (
                    <div key={t.id} className="rounded-lg border border-border p-3.5 flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center text-muted-foreground flex-shrink-0">
                            <FileText size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium truncate">{t.name}</span>
                                {t.archived && (
                                    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                                        Archived
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground">Last edited {fmtDate(t.edited)}</div>
                        </div>
                        <button onClick={() => setPreview(t)} className="h-8 px-3 rounded-lg border border-border text-sm font-medium hover:bg-accent">
                            Preview
                        </button>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                            <button onClick={() => setEditing({ ...t })} className="hover:text-foreground p-1" title="Edit template">
                                <Pencil size={16} />
                            </button>
                            <button onClick={() => printLetter(t)} className="hover:text-foreground p-1" title="Print letter">
                                <Printer size={16} />
                            </button>
                            <button
                                onClick={() => openSend(t)}
                                disabled={!canSend}
                                className="hover:text-primary p-1 disabled:opacity-40 disabled:cursor-not-allowed"
                                title={canSend ? `Email to ${incident.employeeName}` : 'No linked employee on this incident'}
                            >
                                <Send size={16} />
                            </button>
                            <button
                                onClick={() => onArchive(t.id)}
                                className={`p-1 ${t.archived ? 'hover:text-foreground' : 'hover:text-destructive'}`}
                                title={t.archived ? 'Restore template' : 'Archive template'}
                            >
                                {t.archived ? <RotateCcw size={16} /> : <Trash2 size={16} />}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Template editor */}
            {editing && (
                <Modal
                    title={isNewTemplate ? 'New Template' : 'Edit Template'}
                    onClose={() => setEditing(null)}
                    wide
                    footer={
                        <>
                            <button onClick={() => setEditing(null)} className={modalCancelBtn}>Cancel</button>
                            <button onClick={saveEditing} className={modalPrimaryBtn}>Save Template</button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium">Template Name</label>
                            <input
                                value={editing.name}
                                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                placeholder="e.g., RTW Offer Letter"
                                className={`${inputCls} mt-1.5`}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Letter Body</label>
                            <textarea
                                rows={12}
                                value={editing.body}
                                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                                placeholder="Write the letter, inserting merge fields where needed..."
                                className="w-full mt-1.5 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm"
                            />
                        </div>
                        <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1.5">
                                Merge fields — click to insert; replaced with incident data when generated.
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {LETTER_FIELDS.map((f) => (
                                    <button
                                        key={f.tag}
                                        type="button"
                                        title={f.label}
                                        onClick={() => setEditing({ ...editing, body: `${editing.body}{{${f.tag}}}` })}
                                        className="rounded-md border border-border bg-card px-2 py-1 text-[11px] font-mono text-muted-foreground hover:bg-accent hover:text-foreground"
                                    >
                                        {`{{${f.tag}}}`}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Merged letter preview */}
            {preview && (
                <Modal
                    title={preview.name}
                    onClose={() => setPreview(null)}
                    wide
                    footer={
                        <>
                            <button onClick={() => setPreview(null)} className={modalCancelBtn}>Close</button>
                            <button onClick={() => printLetter(preview)} className={modalPrimaryBtn}>
                                <span className="inline-flex items-center gap-1.5"><Printer size={15} /> Print</span>
                            </button>
                        </>
                    }
                >
                    <div className="rounded-lg border border-border bg-muted/20 px-5 py-4">
                        <p className="text-xs text-muted-foreground mb-3">
                            Generated for {incident.incidentNumber} · {incident.employeeName}
                        </p>
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                            {renderLetterBody(preview.body, incident)}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Send confirmation */}
            {sendTarget && (
                <Modal
                    title="Send Letter"
                    onClose={() => setSendTarget(null)}
                    footer={
                        <>
                            <button onClick={() => setSendTarget(null)} disabled={sending} className={modalCancelBtn}>Cancel</button>
                            <button onClick={doSend} disabled={sending || !sendSubject.trim()} className={modalPrimaryBtn}>
                                {sending ? 'Sending…' : 'Send Letter'}
                            </button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium">To</label>
                            <input value={`${incident.employeeName}${canSend ? '' : ' (no linked employee)'}`} readOnly className={`${inputCls} mt-1.5 text-muted-foreground`} />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Subject</label>
                            <input
                                value={sendSubject}
                                onChange={(e) => setSendSubject(e.target.value)}
                                className={`${inputCls} mt-1.5`}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Letter</label>
                            <div className="mt-1.5 rounded-lg border border-border bg-muted/20 px-3 py-2.5 max-h-56 overflow-y-auto">
                                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                                    {renderLetterBody(sendTarget.body, incident)}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};
